export const POS_UI_PREFERENCES_NAMESPACE = 'pos:sales';
export const POS_UI_PREFERENCES_KEY = `${POS_UI_PREFERENCES_NAMESPACE}:ui-preferences`;
/**
 * Version history:
 * - v1: collapsed panels (`lineNotes`, `paymentsPanel`, `discountsPanel`) and `preferredPaymentMethodId` at
 *   the root level.
 * - v2: collapsed panels renamed to `customer/payments/shipping/discounts` and payment preferences grouped
 *   under `payments.preferredMethodId`.
 */
export const POS_UI_PREFERENCES_VERSION = 2;
export const POS_UI_PREFERENCES_EXPIRATION_MS = 1000 * 60 * 60 * 24 * 45; // 45 días

export type PosShortcutAction =
  | 'focusSearch'
  | 'openAdvancedSearch'
  | 'openDiscounts'
  | 'openPayment'
  | 'confirm';

export interface PosShortcutDefinition {
  key: string;
  shift?: boolean;
  alt?: boolean;
  ctrl?: boolean;
  meta?: boolean;
  /** Cuando es `true`, acepta Ctrl o Meta indistintamente. */
  system?: boolean;
}

export type PosShortcutMap = Record<PosShortcutAction, PosShortcutDefinition[]>;

export interface PosUiCollapsedState {
  customer: boolean;
  payments: boolean;
  shipping: boolean;
  discounts: boolean;
}

export interface PosUiPaymentPreferences {
  preferredMethodId: number | null;
}

export interface PosUiPreferences {
  lastVariantByItemId: Record<number, number | null>;
  lastProfessionalByItemId: Record<number, number | null>;
  collapsed: PosUiCollapsedState;
  payments: PosUiPaymentPreferences;
  orderNoteDraft: string;
  lastCustomerId: number | null;
  bottomBar: {
    isPinned: boolean;
    showQuickActions: boolean;
  };
  layout: {
    mobileMode: 'auto' | 'compact' | 'expanded';
  };
  shortcuts: PosShortcutMap;
}

export interface PosUiPreferencesEnvelope {
  version: number;
  updatedAt: number;
  preferences: PosUiPreferences;
}

const baseShortcutMap: PosShortcutMap = {
  focusSearch: [
    { key: 'f2' },
    { key: 'k', system: true },
  ],
  openAdvancedSearch: [
    { key: 'k', system: true, shift: true },
  ],
  openDiscounts: [{ key: 'f6' }],
  openPayment: [{ key: 'f8' }],
  confirm: [{ key: 'enter' }],
};

Object.freeze(baseShortcutMap);
Object.values(baseShortcutMap).forEach((definitions) => Object.freeze(definitions));

export const DEFAULT_POS_SHORTCUTS: Readonly<PosShortcutMap> = baseShortcutMap;

export const cloneShortcutDefinition = (definition: PosShortcutDefinition): PosShortcutDefinition => ({
  key: definition.key,
  shift: definition.shift,
  alt: definition.alt,
  ctrl: definition.ctrl,
  meta: definition.meta,
  system: definition.system,
});

export const cloneShortcutMap = (map: PosShortcutMap): PosShortcutMap => {
  return Object.fromEntries(
    Object.entries(map).map(([action, definitions]) => [
      action,
      definitions.map((definition) => cloneShortcutDefinition(definition)),
    ]),
  ) as PosShortcutMap;
};

export const createDefaultPosUiPreferences = (): PosUiPreferences => ({
  lastVariantByItemId: {},
  lastProfessionalByItemId: {},
  collapsed: {
    customer: false,
    payments: false,
    shipping: true,
    discounts: false,
  },
  payments: {
    preferredMethodId: null,
  },
  orderNoteDraft: '',
  lastCustomerId: null,
  bottomBar: {
    isPinned: true,
    showQuickActions: true,
  },
  layout: {
    mobileMode: 'auto',
  },
  shortcuts: cloneShortcutMap(baseShortcutMap),
});

